import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Phone, Mail } from 'lucide-react';
import { mockData, generateWhatsAppLink } from '../mock';
import Seo from '../components/Seo';
import { SITE_URL, SITE_NAME } from '../lib/seo/constants';
import { useCatalogueResource } from '../hooks/useCatalogueResource';
import { parsePriceAmount } from '../hooks/useRazorpayCheckout';

const ProgramDetail = () => {
  const { programId } = useParams();
  const navigate = useNavigate();

  const localProgram =
    mockData.programs.find((p) => p.id === parseInt(programId, 10)) || null;

  const { data: program } = useCatalogueResource(
    `/api/programs/${programId}`,
    localProgram
  );

  if (!program) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ paddingTop: '100px' }}
      >
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Program Not Found</h1>
          <Link to="/" className="text-brand-primary hover:text-brand-secondary">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  // Premium price strings ("Starting From ₹X", "Custom Investment", etc.) — extract numeric amount
  const priceAmount = parsePriceAmount(program.price);
  const isCustomInvestment = priceAmount === 0;
  const itemList = program.outcomes || program.features || [];

  return (
    <div className="min-h-screen" style={{ paddingTop: '100px' }} data-testid="program-detail-page">
      <Seo
        title={program.name}
        description={program.description}
        path={`/pricing/${program.id}`}
        image={program.image}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: program.name,
          provider: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          description: program.description,
          serviceType: program.category,
          areaServed: 'IN',
          offers: {
            '@type': 'Offer',
            priceCurrency: 'INR',
            price: priceAmount || undefined,
            url: `${SITE_URL}/pricing/${program.id}`,
          },
        }}
      />

      {/* Back Button */}
      <div className="container mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-brand-primary transition-colors"
          data-testid="program-detail-back-btn"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
      </div>

      {/* Hero */}
      <div className="relative">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative">
              {program.image ? (
                <img
                  src={program.image}
                  alt={program.name}
                  className="w-full h-[400px] object-cover rounded-3xl"
                  style={{ boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)' }}
                />
              ) : (
                <div
                  className="w-full h-[400px] rounded-3xl bg-gradient-to-br from-brand-primary/20 to-brand-accent/20 backdrop-blur-sm border border-white/10 flex items-center justify-center overflow-hidden"
                  style={{ boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)' }}
                >
                  <div className="text-center p-8">
                    <p className="text-text-light text-sm">Image coming soon</p>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div>
              <div className="inline-flex flex-wrap items-center gap-3 px-4 py-2 rounded-full bg-brand-light border border-brand-primary/30 text-brand-primary text-sm font-semibold mb-4">
                <span>{program.duration}</span>
                {program.category && (
                  <>
                    <span>•</span>
                    <span>{program.category}</span>
                  </>
                )}
              </div>

              <h1
                className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-white via-brand-primary to-brand-accent bg-clip-text text-transparent"
                data-testid="program-detail-name"
              >
                {program.name}
              </h1>

              <div className="mb-8">
                <div className="flex items-baseline gap-3 mb-4 flex-wrap">
                  <span
                    className="text-4xl md:text-5xl font-black text-brand-accent"
                    style={{ textShadow: '0 0 20px rgba(255, 215, 0, 0.5)' }}
                    data-testid="program-detail-price"
                  >
                    {program.price}
                  </span>
                </div>
                {program.ideal && (
                  <p
                    className="text-text-secondary italic"
                    data-testid="program-detail-ideal"
                  >
                    {program.ideal}
                  </p>
                )}
                {program.description && (
                  <p className="text-text-light mt-4">{program.description}</p>
                )}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                {isCustomInvestment ? (
                  <>
                    <a
                      href={generateWhatsAppLink(
                        `Hi! I'd like to book a consultation for ${program.name}.`
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-cta"
                      data-testid="program-detail-book-consultation-btn"
                    >
                      {program.cta || 'Book Consultation'}
                    </a>
                    <Link to="/assessment" className="btn-secondary" data-testid="program-detail-assessment-link">
                      Take Capability Assessment
                    </Link>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => navigate('/checkout', { state: { program } })}
                      className="btn-cta"
                      data-testid="program-detail-checkout-btn"
                    >
                      Proceed to Checkout
                    </button>
                    <Link to="/#contact" className="btn-secondary" data-testid="program-detail-contact-link">
                      Ask Questions
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features / Outcomes */}
      {itemList.length > 0 && (
        <div className="bg-subtle py-16">
          <div className="container mx-auto px-4">
            <h2 className="section-title text-center mb-12">What&apos;s Included</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {itemList.map((item, index) => (
                <div
                  key={`feature-${program.id}-${index}`}
                  className="network-card p-6 flex items-start gap-4"
                  data-testid={`program-feature-${index}`}
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-primary to-brand-accent flex items-center justify-center flex-shrink-0 mt-1">
                    <Check size={16} className="text-white" />
                  </div>
                  <p className="text-text-secondary">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contact / CTA */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="network-card p-12 text-center">
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-white to-brand-primary bg-clip-text text-transparent">
              Ready to Transform?
            </h2>
            <p className="text-text-secondary mb-8">
              Have questions about this program? Our team is here to help you choose the right pathway for your goals.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <a
                href={`tel:${mockData.contact.phone}`}
                className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10 hover:border-brand-primary/50 transition-all"
              >
                <Phone size={20} className="text-brand-primary" />
                <span className="text-white">{mockData.contact.phone}</span>
              </a>

              <a
                href={`mailto:${mockData.contact.email}`}
                className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10 hover:border-brand-primary/50 transition-all"
              >
                <Mail size={20} className="text-brand-accent" />
                <span className="text-white">{mockData.contact.email}</span>
              </a>
            </div>

            <a
              href={generateWhatsAppLink(
                `Hi! I'd like to learn more about ${program.name}.`
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cta inline-flex"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramDetail;
